// web/src/lib/sanitizeScript.ts
// Deterministic hygiene for model-generated script text, applied CLIENT-SIDE at the two
// pre-TTS seams (runGeneration photo, deckPipeline deck) BEFORE swapSoundalikes — so the
// spoken audio, captions, and STT alignment all derive from the same cleaned string.
//
// Why this exists: the script prompts impose profanity-count quotas, and a thinking model
// occasionally narrates its self-check into the answer text ("[1] … Let's check profanity
// counts: …") — documented verbatim in functions/src/generateRoast.ts. The server-side
// mitigations (OUTPUT_GUARD prompt text, thinking_level:"low") are probabilistic; this is
// the deterministic backstop. It also strips stage directions (*mic drop*) and markdown
// residue that TTS would otherwise vocalize.
//
// CRITICAL: pre-starred curses ("f*ck", "sh*t") are legitimate model output that the bleep
// dictionary (ALLOW_FIRST_MS) expects — every rule here is word-boundary-anchored so an
// in-word "*" is never touched.

/** Sentences that are quota/self-check narration, never roast content. High-precision by
 *  design — "requirements"/"checklist"/"quota" are excluded because deck roasts legitimately
 *  mock slides containing those words. Tune via the [sanitize] console breadcrumbs. */
const META_SENTENCE = [/\bprofanit/i, /\bself[- ]?check/i, /\bword\s?count\b/i];

/** Leading preamble line ("Here's your roast:", "Note: …") — only ever the FIRST line. */
const PREAMBLE = /^\s*(?:here[''`]?s\b[^\n]*:|note:[^\n]*|okay,[^\n]*:)\s*\n+/i;

/** Paired [ ... ] block (audio tag, index, aside). */
const BRACKET_BLOCK = /\[[^\]\n]*\]/g;
/** Stray unmatched bracket plus whatever token it's glued to. */
const STRAY_OPEN = /\[[^\s\]]*/g;
const STRAY_CLOSE = /[^\s[]*\]/g;
/** *stage direction* — the opening * must start a token and the closing * must end one,
 *  so in-word stars (f*ck this sh*t) can never pair across words. */
const STAGE_DIRECTION = /(?<=^|\s)\*[^*\n]{1,60}\*(?=$|[\s.,!?;:])/g;
/** Markdown emphasis residue at token edges only (in-word * and _ are preserved). */
const EDGE_STARS = /(?<![A-Za-z0-9])\*+|\*+(?![A-Za-z0-9])/g;
const EDGE_UNDERSCORES = /(?<![A-Za-z0-9])_+|_+(?![A-Za-z0-9])/g;
/** Leading list markers ("1. ", "2) ", "- ", "• ") at the start of any line. */
const LIST_MARKER = /^[ \t]*(?:\d{1,3}[.)]|[-•])[ \t]+/gm;

export interface SanitizeResult {
  text: string;
  /** Human-readable fragments that were removed (for the [sanitize] breadcrumb log). */
  removed: string[];
}

export function sanitizeScriptDetailed(raw: string): SanitizeResult {
  const removed: string[] = [];
  const record = (m: string) => {
    const t = m.trim();
    if (t) removed.push(t);
    return " ";
  };

  let text = raw;

  // 1. Leading preamble line ("Here's your roast:" / "Note: …").
  text = text.replace(PREAMBLE, record);

  // 2. Bracketed blocks, then stray unmatched brackets.
  text = text.replace(BRACKET_BLOCK, record);
  text = text.replace(STRAY_OPEN, record);
  text = text.replace(STRAY_CLOSE, record);

  // 3. Stage directions, then markdown residue (word-boundary-anchored; in-word * survives).
  text = text.replace(STAGE_DIRECTION, record);
  text = text.replace(EDGE_STARS, "");
  text = text.replace(EDGE_UNDERSCORES, "");

  // 4. List markers at line starts.
  text = text.replace(LIST_MARKER, " ");

  // 5. Self-check meta sentences (whole-sentence removal).
  const sentences = text.split(/(?<=[.!?])\s+/);
  const kept = sentences.filter((s) => {
    if (META_SENTENCE.some((re) => re.test(s))) {
      const t = s.trim();
      if (t) removed.push(t);
      return false;
    }
    return true;
  });
  text = kept.join(" ");

  // 6. Collapse the whitespace the removals left behind.
  text = text.replace(/\s+/g, " ").trim();

  return { text, removed };
}

export function sanitizeScript(raw: string): string {
  return sanitizeScriptDetailed(raw).text;
}

/** Seam helper: sanitize + emit a breadcrumb when anything was actually removed, so
 *  intermittent leaks leave evidence in the console instead of vanishing into the audio. */
export function sanitizeWithLog(raw: string, label: string): string {
  const { text, removed } = sanitizeScriptDetailed(raw);
  if (removed.length > 0) {
    console.warn(`[sanitize] ${label}: removed ${removed.length} artifact(s)`, removed);
  }
  return text;
}
